import template from "./notice-modal.html";
import "./notice-modal.less";
import { isTextMarkdown } from "../../../components/notices/isTextMarkdown";
import marked from "marked";
import moment from "moment";
import { globs } from "../../../services/globs";
import markdownEditor from "../../../components/markdown-editor/markdown-editor.js";

function controller(
	noticeAdminService,
	appState,
	loadingService,
	toastr,
	$logger,
	$sce
) {
	const ctrl = this;
	ctrl.promiseTracker = loadingService.getTracker();
	ctrl.canUpdate = globs.notices.update;

	ctrl.severityOptions = [
		{ code: "INFO", name: "Info" },
		{ code: "WARNING", name: "Warning" },
		{ code: "URGENT", name: "Urgent" },
		{ code: "CRITICAL", name: "Critical" }
	];

	ctrl.intervalParts = { days: 0, hours: 0, minutes: 0 };

	function parseInterval(str) {
		if(!str) {
			return { days: null, hours: null, minutes: null };
		}
		// Format: "d.HH:mm:ss" or "HH:mm:ss"
		const dotParts = str.split(".");
		let days = 0;
		let timePart = str;
		if(dotParts.length === 2) {
			days = parseInt(dotParts[0], 10) || 0;
			timePart = dotParts[1];
		}
		const timePieces = timePart.split(":");
		const hours = parseInt(timePieces[0], 10) || 0;
		const minutes = parseInt(timePieces[1], 10) || 0;
		return { days, hours, minutes };
	}

	function buildInterval(parts) {
		const d = parts.days || 0;
		const h = String(parts.hours || 0).padStart(2, "0");
		const m = String(parts.minutes || 0).padStart(2, "0");
		return `${d}.${h}:${m}:00`;
	}

	ctrl.onIntervalPartChange = () => {
		const p = ctrl.intervalParts;
		if(!p.days && !p.hours && !p.minutes) {
			ctrl.editData.Interval = null;
		}
		else {
			ctrl.editData.Interval = buildInterval(p);
		}
	};

	ctrl.formatInterval = (str) => {
		const p = parseInterval(str);
		const parts = [];
		if(p.days > 0) { parts.push(p.days + (p.days === 1 ? " day" : " days")); }
		if(p.hours > 0) { parts.push(p.hours + (p.hours === 1 ? " hour" : " hours")); }
		if(p.minutes > 0) { parts.push(p.minutes + (p.minutes === 1 ? " minute" : " minutes")); }
		return parts.length > 0 ? parts.join(", ") : "None";
	};

	ctrl.$onInit = () => {
		ctrl.form = appState.forms.selected;

		if(ctrl.resolve) {
			ctrl.notice = ctrl.resolve.notice;
			ctrl.mode = ctrl.resolve.mode || "view";
		}

		if(ctrl.mode === "create") {
			ctrl.editData = {
				Title: "",
				Content: "",
				BeginInclusive: moment().toISOString(),
				EndExclusive: null,
				Interval: null,
				Severity: ctrl.severityOptions[0]
			};
			ctrl.intervalParts = { days: null, hours: null, minutes: null };
		}
		else if(ctrl.notice) {
			ctrl.editData = {
				Title: ctrl.notice.Title,
				Content: ctrl.notice.Content,
				BeginInclusive: ctrl.notice.BeginInclusive,
				EndExclusive: ctrl.notice.EndExclusive || null,
				Interval: ctrl.notice.Interval || null,
				Severity: ctrl.severityOptions.find(s => s.code === ctrl.notice.Severity?.Code) || ctrl.severityOptions[0]
			};
			ctrl.intervalParts = parseInterval(ctrl.notice.Interval);

			if(ctrl.mode === "view") {
				ctrl.renderContent();
			}
		}
	};

	ctrl.renderContent = () => {
		if(!ctrl.notice) {
			return;
		}
		if(ctrl.notice.Content && isTextMarkdown(ctrl.notice.Content)) {
			ctrl.renderedContent = $sce.trustAsHtml(marked(ctrl.notice.Content));
		}
		else {
			ctrl.renderedContent = $sce.trustAsHtml(ctrl.notice.Content || "");
		}
	};

	ctrl.switchToEdit = () => {
		ctrl.mode = "edit";
	};

	ctrl.toggleInterval = () => {
		if(!ctrl.editData.Interval) {
			ctrl.intervalParts = { days: 1, hours: 0, minutes: 0 };
			ctrl.editData.Interval = buildInterval(ctrl.intervalParts);
		}
	};

	ctrl.getSeverityIcon = (code) => {
		switch(code) {
			case "INFO": return "zmdi-info";
			case "WARNING": return "zmdi-alert-triangle";
			case "URGENT": return "zmdi-alert-octagon";
			case "CRITICAL": return "zmdi-alert-polygon";
			default: return "";
		}
	};

	ctrl.getStatusLabel = () => {
		if(!ctrl.notice) {
			return "";
		}
		const now = moment();
		const begin = moment(ctrl.notice.BeginInclusive);
		const end = ctrl.notice.EndExclusive ? moment(ctrl.notice.EndExclusive) : null;

		if(end && end.isBefore(now)) {
			return "Expired";
		}
		if(begin.isAfter(now)) {
			return "Scheduled";
		}
		return "Active";
	};

	ctrl.isActive = () => {
		return ctrl.getStatusLabel() === "Active";
	};

	ctrl.expire = () => {
		const payload = {
			Title: ctrl.notice.Title,
			Content: ctrl.notice.Content,
			BeginInclusive: ctrl.notice.BeginInclusive,
			EndExclusive: moment().toISOString(),
			Interval: ctrl.notice.Interval || null,
			Severity: { Code: ctrl.notice.Severity?.Code }
		};

		const promise = noticeAdminService.updateNotice(ctrl.form.Id, ctrl.notice.Id, payload)
			.then((updated) => {
				toastr.success("Notice expired successfully.");
				ctrl.notice = updated;
				ctrl.renderContent();
			})
			.catch((err) => {
				$logger.error("Failed to expire notice", err);
				toastr.error("Failed to expire notice.");
			});
		ctrl.promiseTracker.addPromise(promise);
	};

	ctrl.save = () => {
		if(!ctrl.editData.Title || !ctrl.editData.Content) {
			toastr.error("Title and Content are required.");
			return;
		}

		if(!ctrl.editData.BeginInclusive) {
			toastr.error("Start Date is required.");
			return;
		}

		if(ctrl.editData.EndExclusive && moment(ctrl.editData.EndExclusive).isBefore(moment(ctrl.editData.BeginInclusive))) {
			toastr.error("End Date must be after Start Date.");
			return;
		}

		const payload = {
			Title: ctrl.editData.Title,
			Content: ctrl.editData.Content,
			BeginInclusive: moment(ctrl.editData.BeginInclusive).toISOString(),
			EndExclusive: ctrl.editData.EndExclusive
				? moment(ctrl.editData.EndExclusive).toISOString()
				: null,
			Interval: ctrl.editData.Interval || null,
			Severity: { Code: ctrl.editData.Severity.code }
		};

		if(ctrl.mode === "create") {
			const promise = noticeAdminService.createNotice(ctrl.form.Id, payload)
				.then((created) => {
					toastr.success("Notice created successfully.");
					ctrl.notice = created;
					ctrl.mode = "view";
					ctrl.renderContent();
				})
				.catch((err) => {
					$logger.error("Failed to create notice", err);
					toastr.error("Failed to create notice.");
				});
			ctrl.promiseTracker.addPromise(promise);
		}
		else if(ctrl.mode === "edit") {
			const promise = noticeAdminService.updateNotice(ctrl.form.Id, ctrl.notice.Id, payload)
				.then((updated) => {
					toastr.success("Notice updated successfully.");
					ctrl.notice = updated;
					ctrl.mode = "view";
					ctrl.renderContent();
				})
				.catch((err) => {
					$logger.error("Failed to update notice", err);
					toastr.error("Failed to update notice.");
				});
			ctrl.promiseTracker.addPromise(promise);
		}
	};

	ctrl.cancel = () => {
		if(ctrl.mode === "edit" && ctrl.notice) {
			ctrl.mode = "view";
			ctrl.editData = {
				Title: ctrl.notice.Title,
				Content: ctrl.notice.Content,
				BeginInclusive: ctrl.notice.BeginInclusive,
				EndExclusive: ctrl.notice.EndExclusive || null,
				Interval: ctrl.notice.Interval || null,
				Severity: ctrl.severityOptions.find(s => s.code === ctrl.notice.Severity?.Code) || ctrl.severityOptions[0]
			};
			ctrl.intervalParts = parseInterval(ctrl.notice.Interval);
			ctrl.renderContent();
		}
		else {
			ctrl.modalInstance.dismiss("cancel");
		}
	};

	ctrl.close = () => {
		ctrl.modalInstance.close(ctrl.notice);
	};
}

export default angular
	.module("vesl.screens.notices.modal", [markdownEditor])
	.component("noticeModal", {
		template,
		controller,
		bindings: {
			resolve: "<",
			modalInstance: "<"
		}
	}).name;
